<template>
  <ol class="lp-evo">
    <li v-for="w in works" :key="w.id" :class="w.public ? null : 'is-private'">
      <component :is="w.url ? 'a' : 'div'" class="lp-evo-card" :href="w.url || undefined" :rel="w.url ? 'noopener' : undefined">
        <span class="lp-evo-img"><img v-if="w.img" loading="lazy" decoding="async" width="640" height="640" :src="w.img" alt="" /></span>
        <span class="lp-evo-text">
          <b>{{ name(w) }}</b>
          <span class="lp-evo-meta"><time :datetime="w.last" :title="tpeDay(w.last)">{{ ago(w.last) }}</time><template v-if="!w.public"> · <span class="lp-evo-priv">{{ zh ? '未公開' : 'Private' }}</span></template></span>
        </span>
      </component>
    </li>
  </ol>
</template>

<script setup>
// 正在演化：實驗室 /api/evolving.json 的名單（scripts/lib/lab-evolving.mjs），順序照實驗室。
// 公開的件連到實驗室的作品頁。未公開的只有縮圖、名字、時間與「未公開」，不是連結（實驗室上也點不進去）。
// 未公開照原色顯示、只靠標示（實驗室同一條規則，哲宇 2026-10-03：「不要灰調，有標示就好」）。
// 單一根節點（ol）：頁面的 scoped 樣式與屬性才落得到它身上。
// today：台北日期 YYYY-MM-DD。預渲染時是建置那天，讀者打開時是當天（main.js 用 createApp 重新掛載，不是 hydrate，兩邊不必一致）。
const props = defineProps({
  works: { type: Array, required: true },
  zh: { type: Boolean, default: false },
  today: { type: String, required: true },
})
const tpeDay = (t) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date(t))
const name = (w) => (props.zh ? w.titleZh || w.title : w.title || w.titleZh) || w.id
// 跟實驗室同一套說法：今天、昨天、N 天前（台北日期相減，不看幾點）
function ago(last) {
  const n = Math.max(0, Math.round((Date.parse(props.today) - Date.parse(tpeDay(last))) / 864e5))
  if (props.zh) return n === 0 ? '今天' : n === 1 ? '昨天' : `${n} 天前`
  return n === 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`
}
</script>

<style scoped>
/* 4 欄（桌機，8 件剛好兩列）、手機 2 欄。卡片沒有框：縮圖直角（DESIGN.md §十八）＋下面一行名字、一行時間 */
.lp-evo { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 28px 16px; }
.lp-evo li { min-width: 0; }
.lp-evo-card { display: flex; flex-direction: column; gap: 12px; color: var(--ink); text-decoration: none; }
.lp-evo-img { display: block; aspect-ratio: 1 / 1; overflow: hidden; background: var(--bg1); }
.lp-evo-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform var(--dur-scene) var(--ease-out); }
a.lp-evo-card:hover .lp-evo-img img, a.lp-evo-card:focus-visible .lp-evo-img img { transform: scale(1.03); }
a.lp-evo-card:hover b { color: var(--gold); }
a.lp-evo-card:focus-visible { outline: 2px solid var(--gold); outline-offset: 4px; }
.lp-evo-text { display: flex; flex-direction: column; gap: 4px; }
.lp-evo-text b { font-family: var(--font-display); font-weight: 400; font-size: 19px; line-height: 1.4; transition: var(--t-hover); }
.lp-evo-meta { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; color: var(--ink-3); letter-spacing: 0.02em; }
.lp-evo-priv { color: var(--ink-2); }
.is-private .lp-evo-card { cursor: default; }
@media (max-width: 700px) {
  .lp-evo { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px 10px; }
  .lp-evo-text b { font-size: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-evo-img img { transition: none; }
  a.lp-evo-card:hover .lp-evo-img img { transform: none; }
}
</style>
